/**
 * Voting - port from `1116/omega-club/dashboard/src/components/Voting.jsx`.
 * Reads `roster` from `useTenantData(slug)` (added to TenantConfig in this
 * ticket via tenant_users + users join).
 *
 * Per CHAP-85 (Phase 1C #51).
 *
 * Parity notes vs omega-club:
 * - **Meeting cadence** is currently a hardcoded default. Until per-tenant
 *   cadence lands (proposed schema: `tenants.meeting_cadence jsonb`), every
 *   tenant gets the omega-zeta default (biweekly Wednesdays 7pm CT,
 *   secretary Austin Osborn). Separate ticket queued for the schema field.
 * - **Votes** are persisted server-side via `votes` + `vote_responses`
 *   (CHAP-85). Signed-hash integrity per CHAP-208 / plan #183-184; cast
 *   writes reuse `computeVoteHash` so the CHAP-208 verify endpoint stays
 *   green.
 * - **Roster** is sourced from the new `data.roster` field - joined
 *   tenant_users + users names, role, tier, committees,
 *   chapterInitiationNumber. No PII (email/picture); /api/me carries
 *   that for the authenticated user only.
 */
import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTenantData } from '../hooks/useTenantData.js';
import { TenantLoading, TenantError } from '../components/TenantDataStates.js';
import { TierGuard } from '../components/RoleGuard.js';
import { useVotes, useCastVote, useCreateVote, useSetVoteStatus, type VoteListItem, type VoteMode, type VoteStatus } from '../hooks/useVotes.js';
import type { TenantRosterMember, CustomerTier } from '../../shared/types.js';

// CHAP-85 - meeting cadence default (per-tenant config field is a follow-up)
const DEFAULT_MEETING_CADENCE = {
  frequency: 'biweekly',
  scheduledBy: 'Austin Osborn',
  scheduledByRole: 'Secretary',
  dayOfWeek: 'Wednesday',
  typicalTime: '7:00 PM CT',
};

const COMMITTEE_LABELS: Record<string, string> = {
  steering: 'Steering',
  building: 'Building',
  finance: 'Finance',
  fundraising: 'Fundraising',
  alumni_communications: 'Alumni Comms',
};

const TIER_LABEL: Record<CustomerTier, string> = {
  cabinet: 'Cabinet',
  board: 'Board',
  voter: 'Voter',
  observer: 'Observer',
};

function deadlineDaysRemaining(iso: string | null): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

export default function Voting() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { data, isLoading, error, refetch } = useTenantData(slug);
  const votesQuery = useVotes(slug);
  const castVote = useCastVote(slug);
  const createVote = useCreateVote(slug);
  const setVoteStatus = useSetVoteStatus(slug);
  const [showCreate, setShowCreate] = useState(false);

  if (isLoading) return <TenantLoading />;
  if (error) return <TenantError message={String(error)} onRetry={() => refetch()} />;
  if (!data) return <PageState label="No data" />;

  const allVotes = votesQuery.data?.votes ?? [];
  const openVotes = allVotes.filter((v) => v.status === 'open');
  const closedVotes = allVotes.filter((v) => v.status !== 'open');

  return (
    <div className="space-y-4 min-w-0">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex items-start justify-between flex-wrap gap-3 min-w-0">
          <div className="min-w-0 flex-1">
            <h2 className="text-navy text-xl sm:text-2xl font-bold">Voting</h2>
            <p className="text-neutral text-sm mt-1">Proxy voting + roster for {data.tenant.displayName}</p>
          </div>
          <TierGuard tier="cabinet">
            <button
              type="button"
              onClick={() => setShowCreate((s) => !s)}
              className="text-xs sm:text-sm px-3 py-1.5 rounded-lg bg-orange text-white font-semibold hover:bg-orange/90 transition-colors flex-shrink-0"
            >
              {showCreate ? 'Cancel' : 'New vote'}
            </button>
          </TierGuard>
        </div>
      </div>

      {showCreate && (
        <TierGuard tier="cabinet">
          <CreateVoteForm
            onCreate={async (body) => {
              await createVote.mutateAsync(body);
              setShowCreate(false);
            }}
            pending={createVote.isPending}
            error={createVote.error}
          />
        </TierGuard>
      )}

      {/* Meeting cadence pill */}
      <CadencePill />

      {/* Cabinet + Board roster panel */}
      <RosterPanel roster={data.roster} />

      {/* Votes */}
      {votesQuery.isLoading ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 text-center text-sm text-neutral">
          Loading votes…
        </div>
      ) : votesQuery.error ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 text-center text-sm text-red-700">
          {String(votesQuery.error)}
        </div>
      ) : (
        <>
          <div className="space-y-3">
            <h3 className="text-xs uppercase tracking-wider font-semibold text-gray-400">Active votes ({openVotes.length})</h3>
            {openVotes.length > 0 ? (
              openVotes.map((vote) => (
                <VoteCard
                  key={vote.id}
                  vote={vote}
                  onCast={(optionValue) => castVote.mutate({ id: vote.id, optionValue })}
                  castPending={castVote.isPending && castVote.variables?.id === vote.id}
                  castError={castVote.variables?.id === vote.id ? castVote.error : null}
                  onSetStatus={(status) => setVoteStatus.mutate({ id: vote.id, status })}
                />
              ))
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 text-center text-sm text-neutral italic">
                No active votes.
              </div>
            )}
          </div>

          {closedVotes.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xs uppercase tracking-wider font-semibold text-gray-400">Closed / tabled ({closedVotes.length})</h3>
              {closedVotes.map((vote) => (
                <VoteCard
                  key={vote.id}
                  vote={vote}
                  onCast={() => {}}
                  castPending={false}
                  castError={null}
                  onSetStatus={(status) => setVoteStatus.mutate({ id: vote.id, status })}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function VoteCard({
  vote,
  onCast,
  castPending,
  castError,
  onSetStatus,
}: {
  vote: VoteListItem;
  onCast: (optionValue: string) => void;
  castPending: boolean;
  castError: Error | null;
  onSetStatus: (status: VoteStatus) => void;
}) {
  const days = deadlineDaysRemaining(vote.deadline);
  const disabled = vote.status !== 'open' || vote.hasVoted || castPending;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 min-w-0">
      <div className="flex items-start justify-between gap-3 flex-wrap min-w-0">
        <div className="min-w-0 flex-1">
          <h4 className="text-navy font-bold text-sm sm:text-base">{vote.title}</h4>
          {vote.description && <p className="text-neutral text-xs mt-1">{vote.description}</p>}
          <p className="text-[10px] uppercase tracking-wider text-gray-400 mt-1">
            {vote.mode === 'anonymous' ? 'Anonymous ballot' : 'Attributed ballot'} · {vote.totalResponses} response{vote.totalResponses === 1 ? '' : 's'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
          {days != null && (
            <span
              className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${
                days <= 3 ? 'bg-red-100 text-red-700' : days <= 7 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {days >= 0 ? `${days}d left` : 'past deadline'}
            </span>
          )}
          <span
            className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${
              vote.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
            }`}
          >
            {vote.status}
          </span>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {vote.options.map((opt) => {
          const isMine = vote.hasVoted && vote.tally[opt] != null && vote.status !== 'open';
          const pct = vote.totalResponses > 0 ? Math.round(((vote.tally[opt] ?? 0) / vote.totalResponses) * 100) : 0;
          return (
            <button
              key={opt}
              type="button"
              disabled={disabled}
              onClick={() => onCast(opt)}
              title={vote.hasVoted ? 'You already voted on this ballot' : undefined}
              className={`text-xs sm:text-sm px-3 py-1.5 rounded-lg border transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                isMine ? 'bg-orange text-white border-orange font-semibold' : 'bg-white text-navy border-gray-200 hover:border-orange'
              }`}
            >
              {opt}
              {vote.totalResponses > 0 && <span className="ml-1.5 text-[10px] opacity-75">{vote.tally[opt] ?? 0} · {pct}%</span>}
            </button>
          );
        })}
      </div>

      {vote.hasVoted && (
        <p className="text-[11px] text-gray-500 italic mt-2">You already cast a ballot on this vote.</p>
      )}
      {castError && (
        <p className="text-[11px] text-red-600 mt-2">{castError.message}</p>
      )}

      <TierGuard tier="cabinet">
        {vote.status === 'open' && (
          <div className="mt-3 flex flex-wrap gap-2 border-t border-gray-100 pt-3">
            <button
              type="button"
              onClick={() => onSetStatus('closed')}
              className="text-[11px] px-2.5 py-1 rounded-lg border border-gray-200 text-navy hover:border-orange transition-colors"
            >
              Close vote
            </button>
            <button
              type="button"
              onClick={() => onSetStatus('tabled')}
              className="text-[11px] px-2.5 py-1 rounded-lg border border-gray-200 text-navy hover:border-orange transition-colors"
            >
              Table vote
            </button>
          </div>
        )}
      </TierGuard>
    </div>
  );
}

function CreateVoteForm({
  onCreate,
  pending,
  error,
}: {
  onCreate: (body: { title: string; description?: string; options: string[]; mode: VoteMode; deadline?: string }) => Promise<void>;
  pending: boolean;
  error: Error | null;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [mode, setMode] = useState<VoteMode>('attributed');
  const [deadline, setDeadline] = useState('');

  const setOption = (i: number, value: string) => {
    setOptions((prev) => prev.map((o, idx) => (idx === i ? value : o)));
  };
  const addOption = () => setOptions((prev) => (prev.length < 10 ? [...prev, ''] : prev));
  const removeOption = (i: number) => setOptions((prev) => (prev.length > 2 ? prev.filter((_, idx) => idx !== i) : prev));

  const cleanOptions = options.map((o) => o.trim()).filter(Boolean);
  const canSubmit = title.trim().length > 0 && cleanOptions.length >= 2;

  return (
    <form
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 space-y-3 min-w-0"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        await onCreate({
          title: title.trim(),
          description: description.trim() || undefined,
          options: cleanOptions,
          mode,
          deadline: deadline ? new Date(deadline + 'T23:59:59').toISOString() : undefined,
        });
        setTitle('');
        setDescription('');
        setOptions(['', '']);
        setDeadline('');
      }}
    >
      <h3 className="text-navy font-bold text-sm sm:text-base">New vote</h3>
      <div className="space-y-2">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title"
          maxLength={255}
          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
        />
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
          maxLength={4000}
          rows={2}
          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
        />
        <div className="space-y-1.5">
          {options.map((opt, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                value={opt}
                onChange={(e) => setOption(i, e.target.value)}
                placeholder={`Option ${i + 1}`}
                maxLength={64}
                className="flex-1 min-w-0 text-sm border border-gray-200 rounded-lg px-3 py-1.5"
              />
              {options.length > 2 && (
                <button type="button" onClick={() => removeOption(i)} className="text-gray-400 hover:text-red-600 flex-shrink-0" aria-label="Remove option">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
          {options.length < 10 && (
            <button type="button" onClick={addOption} className="text-xs text-orange font-semibold hover:underline">
              + Add option
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <label className="text-xs text-gray-600 flex items-center gap-1.5">
            Mode
            <select value={mode} onChange={(e) => setMode(e.target.value as VoteMode)} className="text-xs border border-gray-200 rounded px-2 py-1">
              <option value="attributed">Attributed</option>
              <option value="anonymous">Anonymous</option>
            </select>
          </label>
          <label className="text-xs text-gray-600 flex items-center gap-1.5">
            Deadline
            <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} className="text-xs border border-gray-200 rounded px-2 py-1" />
          </label>
        </div>
      </div>
      {error && <p className="text-[11px] text-red-600">{error.message}</p>}
      <button
        type="submit"
        disabled={!canSubmit || pending}
        className="text-xs sm:text-sm px-3 py-1.5 rounded-lg bg-orange text-white font-semibold hover:bg-orange/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {pending ? 'Creating…' : 'Create vote'}
      </button>
    </form>
  );
}

function CadencePill() {
  const c = DEFAULT_MEETING_CADENCE;
  return (
    <div className="bg-white rounded-lg border border-gray-200 px-3 sm:px-4 py-2 sm:py-2.5 flex items-center gap-3 flex-wrap text-xs sm:text-sm min-w-0">
      <span className="inline-flex items-center gap-1.5 text-navy font-semibold">
        <svg className="w-4 h-4 text-orange flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
        </svg>
        Meeting cadence
      </span>
      <span className="text-gray-700 capitalize">{c.frequency}</span>
      <span className="text-gray-300">·</span>
      <span className="text-gray-700">
        Scheduled by <span className="text-navy font-semibold">{c.scheduledBy}</span> ({c.scheduledByRole})
      </span>
      <span className="text-gray-300 hidden sm:inline">·</span>
      <span className="text-gray-500 hidden sm:inline">{c.dayOfWeek}s, {c.typicalTime}</span>
    </div>
  );
}

function RosterPanel({ roster }: { roster: TenantRosterMember[] }) {
  const [expanded, setExpanded] = useState(false);

  const groups = useMemo(() => {
    const tiers: CustomerTier[] = ['cabinet', 'board', 'voter', 'observer'];
    return tiers
      .map((t) => ({ tier: t, members: roster.filter((m) => m.tier === t) }))
      .filter((g) => g.members.length > 0);
  }, [roster]);

  const cabinetCount = roster.filter((m) => m.tier === 'cabinet').length;

  if (roster.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 px-3 sm:px-4 py-3 text-xs sm:text-sm text-neutral italic">
        No roster members for this tenant. Seed via `tenant_users` in `apps/web/src/server/db/seed/omega-zeta.ts`.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 min-w-0">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 flex items-center gap-3 flex-wrap text-xs sm:text-sm hover:bg-gray-50 transition-colors text-left"
        aria-expanded={expanded}
      >
        <span className="inline-flex items-center gap-1.5 text-navy font-semibold">
          <svg className="w-4 h-4 text-orange flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
          </svg>
          Cabinet & Board roster
        </span>
        <span className="text-gray-700">{cabinetCount} cabinet</span>
        <span className="text-gray-300">·</span>
        <span className="text-gray-700">{roster.length} total</span>
        <span className="ml-auto text-gray-400 text-xs flex items-center gap-1">
          {expanded ? 'Hide' : 'Show'}
          <svg className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </span>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 px-3 sm:px-4 py-3 space-y-4">
          {groups.map((g) => (
            <div key={g.tier} className="space-y-2">
              <h4 className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                {TIER_LABEL[g.tier]} ({g.members.length})
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {g.members.map((m) => (
                  <div key={m.tenantUserId} className="border border-gray-100 rounded-lg p-2.5 min-w-0">
                    <div className="flex items-start justify-between gap-2 flex-wrap min-w-0">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-navy truncate">{m.name ?? 'Unnamed member'}</p>
                        <p className="text-xs text-gray-600 capitalize truncate">
                          {m.role}{m.chapterInitiationNumber ? ` · #${m.chapterInitiationNumber}` : ''}
                        </p>
                      </div>
                    </div>
                    {m.committees.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {m.committees.map((c) => (
                          <span key={c} className="text-[10px] bg-navy/5 text-navy px-1.5 py-0.5 rounded">
                            {COMMITTEE_LABELS[c] || c}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PageState({ label, detail, tone }: { label: string; detail?: string; tone?: 'error' }) {
  const color = tone === 'error' ? 'text-red-700' : 'text-neutral';
  return (
    <div className="p-8 text-center">
      <p className={`text-sm ${color}`}>{label}</p>
      {detail && <pre className="text-xs text-gray-500 mt-2 whitespace-pre-wrap">{detail}</pre>}
    </div>
  );
}
